// Phase 9A.4 — usePublicSignalCardWatchlist hook.
// Phase 9A.6.1 thinning. Phase 9A.7 tags + sub-hook composition.
//
// Read + mutate consumer of public.signal_card_watchlist for the
// authenticated caller. RLS scopes reads, inserts, updates, and
// deletes to the caller; the INSERT policy further requires that
// the target signal_card row passes the 9A.1 public-read policy.
// The 9A.6 UPDATE policy + column-level grants scope note + tag
// writes to the owner and keep identity columns immutable.
//
// 9A.7: note + tag machinery split into focused sub-hooks
// (usePublicSignalNoteMutations / usePublicSignalTagMutations).
// This file owns the saved-card set, save/remove/toggle, and the
// memoized public surface that composes both sub-hooks.
//
// Trust contract:
//   - No external HTTP. No provider API. No LLM. No PDF.
//   - Reads ONLY from `signal_card_watchlist` via the api module.
//   - Soft-fails on errors. Never throws.
//   - Idempotent save. Auth-gated.

import { useCallback, useEffect, useMemo, useState } from "react";

import { useAuthSession } from "../auth/useAuthSession.ts";
import { getBrowserClient } from "../supabase/browser.ts";
import { usePublicSignalNoteMutations } from "./usePublicSignalNoteMutations.ts";
import { usePublicSignalTagMutations } from "./usePublicSignalTagMutations.ts";
import {
  deleteSavedSignalCard,
  fetchSavedRows,
  insertSavedSignalCard,
} from "./public-signal-watchlist-api.ts";

export interface UsePublicSignalCardWatchlistState {
  savedSignalCardIds: ReadonlySet<string>;
  loading: boolean;
  error: string | null;
  togglingSignalCardId: string | null;
  isSaved: (signalCardId: string) => boolean;
  saveSignalCard: (signalCardId: string) => Promise<{ ok: boolean; error?: string }>;
  removeSignalCard: (signalCardId: string) => Promise<{ ok: boolean; error?: string }>;
  toggleSignalCard: (
    signalCardId: string,
  ) => Promise<{ ok: boolean; saved?: boolean; error?: string }>;
  refetch: () => Promise<void>;
  // Phase 9A.6 — private notes.
  savedNotesBySignalCardId: ReadonlyMap<string, string | null>;
  getNote: (signalCardId: string) => string | null;
  saveNote: (signalCardId: string, note: string) => Promise<{ ok: boolean; error?: string }>;
  clearNote: (signalCardId: string) => Promise<{ ok: boolean; error?: string }>;
  noteUpdatingSignalCardId: string | null;
  noteError: string | null;
  // Phase 9A.7 — private tags.
  savedTagsBySignalCardId: ReadonlyMap<string, string[]>;
  getTags: (signalCardId: string) => string[];
  saveTags: (
    signalCardId: string,
    tags: readonly string[],
  ) => Promise<{ ok: boolean; error?: string }>;
  clearTags: (signalCardId: string) => Promise<{ ok: boolean; error?: string }>;
  tagUpdatingSignalCardId: string | null;
  tagError: string | null;
}

const EMPTY_SET: ReadonlySet<string> = new Set<string>();

export function usePublicSignalCardWatchlist(): UsePublicSignalCardWatchlistState {
  const supabase = useMemo(() => getBrowserClient(), []);
  const { user } = useAuthSession();
  const [saved, setSaved] = useState<ReadonlySet<string>>(EMPTY_SET);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [toggling, setToggling] = useState<string | null>(null);

  const noteMut = usePublicSignalNoteMutations({ supabase, user, saved });
  const tagMut = usePublicSignalTagMutations({ supabase, user, saved });

  const refetch = useCallback(async (): Promise<void> => {
    if (!user) {
      setSaved(EMPTY_SET);
      noteMut.resetNotes();
      tagMut.resetTags();
      setError(null);
      noteMut.resetNoteError();
      tagMut.resetTagError();
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    noteMut.resetNoteError();
    tagMut.resetTagError();
    const r = await fetchSavedRows(supabase);
    if (!r.ok) {
      setSaved(EMPTY_SET);
      noteMut.resetNotes();
      tagMut.resetTags();
      setError(r.error ?? "Could not load saved signal cards.");
      setLoading(false);
      return;
    }
    const nextSet = new Set<string>();
    const nextNotes = new Map<string, string | null>();
    const nextTags = new Map<string, string[]>();
    for (const row of r.rows) {
      nextSet.add(row.signalCardId);
      nextNotes.set(row.signalCardId, row.privateNote);
      nextTags.set(row.signalCardId, row.privateTags);
    }
    setSaved(nextSet);
    noteMut.setNotes(nextNotes);
    tagMut.setTags(nextTags);
    setLoading(false);
    // Sub-hook setters are stable; intentionally omitted from deps to
    // avoid spurious refetches. ESLint rule is suppressed via the
    // shape of useCallback's dep array.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [supabase, user]);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  const isSaved = useCallback((signalCardId: string): boolean => saved.has(signalCardId), [saved]);

  const saveSignalCard = useCallback(
    async (signalCardId: string): Promise<{ ok: boolean; error?: string }> => {
      if (!user) return { ok: false, error: "Not signed in." };
      setToggling(signalCardId);
      const r = await insertSavedSignalCard(supabase, user.id, signalCardId);
      if (!r.ok) {
        setToggling(null);
        return { ok: false, error: r.error };
      }
      const next = new Set(saved);
      next.add(signalCardId);
      setSaved(next);
      setToggling(null);
      return { ok: true };
    },
    [supabase, user, saved],
  );

  const removeSignalCard = useCallback(
    async (signalCardId: string): Promise<{ ok: boolean; error?: string }> => {
      if (!user) return { ok: false, error: "Not signed in." };
      setToggling(signalCardId);
      const r = await deleteSavedSignalCard(supabase, user.id, signalCardId);
      if (!r.ok) {
        setToggling(null);
        return { ok: false, error: r.error };
      }
      const next = new Set(saved);
      next.delete(signalCardId);
      setSaved(next);
      setToggling(null);
      return { ok: true };
    },
    [supabase, user, saved],
  );

  const toggleSignalCard = useCallback(
    async (signalCardId: string): Promise<{ ok: boolean; saved?: boolean; error?: string }> => {
      if (saved.has(signalCardId)) {
        const r = await removeSignalCard(signalCardId);
        return { ...r, saved: false };
      }
      const r = await saveSignalCard(signalCardId);
      return { ...r, saved: true };
    },
    [saved, saveSignalCard, removeSignalCard],
  );

  return useMemo<UsePublicSignalCardWatchlistState>(
    () => ({
      savedSignalCardIds: saved,
      loading,
      error,
      togglingSignalCardId: toggling,
      isSaved,
      saveSignalCard,
      removeSignalCard,
      toggleSignalCard,
      refetch,
      savedNotesBySignalCardId: noteMut.notes,
      getNote: noteMut.getNote,
      saveNote: noteMut.saveNote,
      clearNote: noteMut.clearNote,
      noteUpdatingSignalCardId: noteMut.noteUpdating,
      noteError: noteMut.noteError,
      savedTagsBySignalCardId: tagMut.tags,
      getTags: tagMut.getTags,
      saveTags: tagMut.saveTags,
      clearTags: tagMut.clearTags,
      tagUpdatingSignalCardId: tagMut.tagUpdating,
      tagError: tagMut.tagError,
    }),
    [
      saved,
      loading,
      error,
      toggling,
      isSaved,
      saveSignalCard,
      removeSignalCard,
      toggleSignalCard,
      refetch,
      noteMut,
      tagMut,
    ],
  );
}
